import {
    decodeAsciiEscapes,
    redactContactTokens,
} from "../../../../contact-token-scrub.mjs";
import {
    campaignValueSchema,
    shortLinkCampaignSchema,
    shortLinkReferrerHostSchema,
    shortLinkSourceChannelSchema,
    shortLinkUnattributedReasonSchema,
} from "./resolve-short-link.contract";

export const SHORT_LINK_PRIVATE_NO_STORE_HEADERS = {
    "Cache-Control": "private, no-store",
} as const;

export const SHORT_LINK_ARRIVAL_TTL_MS = 120_000;

const TRUSTED_WAGS_APP_ORIGINS = new Set([
    "https://wags.com",
    "https://www.wags.com",
    "https://staging.wags.com",
]);

const CAMPAIGN_KEYS = [
    "utm_source",
    "utm_medium",
    "utm_campaign",
    "utm_term",
    "utm_content",
] as const;

const CONTACT_TOKEN_PATH = /\/([ck])\/[^/?#]+/giu;
const K_LINK_PATH_PREFIX = /^\/k\/[^/?#]+/iu;

export type ShortLinkCampaign = ReturnType<
    typeof shortLinkCampaignSchema.parse
>;

export type ShortLinkSourceChannel = ReturnType<
    typeof shortLinkSourceChannelSchema.parse
>;

export type ShortLinkRequestAttribution = {
    aliasCampaign?: ShortLinkCampaign;
    sourceReferrerHost?: string;
    sourceChannel: ShortLinkSourceChannel;
};

export type ShortLinkDestination =
    | {
          kind: "bridged";
          href: string;
          redirectRequestId: string;
          slug: string;
      }
    | {
          kind: "unattributed";
          href: string;
          reason: ShortLinkUnattributedReason;
      };

export type ShortLinkUnattributedReason = ReturnType<
    typeof shortLinkUnattributedReasonSchema.parse
>;

export type ShortLinkDestinationAnalyticsContext = {
    destinationUrl: string;
    destinationHost: string;
    destinationPath: string;
};

export function isKLinkPath(pathname: string): boolean {
    return K_LINK_PATH_PREFIX.test(decodeAsciiEscapes(pathname));
}

export function redactContactTokenPaths(value: string) {
    return redactContactTokens(value, CONTACT_TOKEN_PATH);
}

export function shortLinkDestinationAnalyticsContext(
    destinationUrl: string,
): ShortLinkDestinationAnalyticsContext {
    try {
        const parsed = new URL(destinationUrl);
        if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
            throw new Error("Non-web destination");
        }
        const destinationPath = redactContactTokenPaths(parsed.pathname);
        return {
            destinationUrl: `${parsed.origin}${destinationPath}`,
            destinationHost: parsed.hostname.toLowerCase(),
            destinationPath,
        };
    } catch {
        return {
            destinationUrl: "[unavailable destination]",
            destinationHost: "",
            destinationPath: "",
        };
    }
}

function normalizeCampaignValue(value: string) {
    const parsed = campaignValueSchema.safeParse(value);
    return parsed.success ? parsed.data : undefined;
}

export function readShortLinkRequestAttribution({
    search,
    referrer,
}: {
    search: string;
    referrer: string;
}): ShortLinkRequestAttribution {
    const params = new URLSearchParams(search);
    const campaignEntries = CAMPAIGN_KEYS.flatMap((key) => {
        const values = params.getAll(key);
        if (values.length !== 1) return [];
        const value = normalizeCampaignValue(values[0]);
        return value === undefined ? [] : [[key, value] as const];
    });
    const aliasCampaignResult = shortLinkCampaignSchema.safeParse(
        Object.fromEntries(campaignEntries),
    );
    const aliasCampaign =
        aliasCampaignResult.success &&
        Object.keys(aliasCampaignResult.data).length > 0
            ? aliasCampaignResult.data
            : undefined;
    const source = safeReferrerAttribution(referrer);

    return {
        ...(aliasCampaign ? { aliasCampaign } : {}),
        ...(source.host ? { sourceReferrerHost: source.host } : {}),
        sourceChannel: source.channel,
    };
}

export function readShortLinkLocationAttribution(
    requestedHref: string,
    referrer: string,
) {
    const requestedUrl = new URL(requestedHref);
    return readShortLinkRequestAttribution({
        search: requestedUrl.search,
        referrer,
    });
}

function safeReferrerAttribution(referrer: string): {
    host?: string;
    channel: ShortLinkSourceChannel;
} {
    if (!referrer) return { channel: "direct" };

    let host: string;
    try {
        const parsed = new URL(referrer);
        if (
            (parsed.protocol !== "https:" && parsed.protocol !== "http:") ||
            parsed.username.length > 0 ||
            parsed.password.length > 0
        ) {
            return { channel: "unknown" };
        }
        host = parsed.hostname.toLowerCase();
    } catch {
        return { channel: "unknown" };
    }

    if (!host) return { channel: "unknown" };
    const channel = classifyReferrerHost(host);
    return {
        ...(shortLinkReferrerHostSchema.safeParse(host).success
            ? { host }
            : {}),
        channel,
    };
}

function isDomain(host: string, domain: string) {
    return host === domain || host.endsWith(`.${domain}`);
}

function classifyReferrerHost(host: string): ShortLinkSourceChannel {
    if (isDomain(host, "wags.com")) return "internal";
    if (
        isDomain(host, "google.com") ||
        /^google\.[a-z]{2,3}(?:\.[a-z]{2})?$/u.test(host) ||
        ["bing.com", "yahoo.com", "duckduckgo.com", "baidu.com"].some(
            (domain) => isDomain(host, domain),
        )
    ) {
        return "search";
    }
    if (
        [
            "facebook.com",
            "instagram.com",
            "tiktok.com",
            "linkedin.com",
            "x.com",
            "twitter.com",
            "youtube.com",
            "pinterest.com",
            "reddit.com",
        ].some((domain) => isDomain(host, domain))
    ) {
        return "social";
    }
    return "referral";
}

function parseDestination(destinationUrl: string) {
    if (
        destinationUrl !== destinationUrl.trim() ||
        /[\u0000-\u0020\\]/u.test(destinationUrl) ||
        /%(?![0-9a-f]{2})/iu.test(destinationUrl)
    ) {
        return null;
    }

    try {
        return new URL(destinationUrl);
    } catch {
        return null;
    }
}

export function createShortLinkDestination({
    destinationUrl,
    slug,
    now = Date.now(),
    randomId = () => globalThis.crypto.randomUUID(),
}: {
    destinationUrl: string;
    slug: string;
    now?: number;
    randomId?: () => string;
}): ShortLinkDestination {
    const parsedDestination = parseDestination(destinationUrl);
    if (!parsedDestination) {
        return {
            kind: "unattributed",
            href: destinationUrl,
            reason: "invalid_destination",
        };
    }

    if (
        parsedDestination.protocol !== "https:" ||
        parsedDestination.username ||
        parsedDestination.password ||
        !TRUSTED_WAGS_APP_ORIGINS.has(parsedDestination.origin)
    ) {
        return {
            kind: "unattributed",
            href: destinationUrl,
            reason: "untrusted_destination",
        };
    }

    if (destinationUrl.includes("#")) {
        return {
            kind: "unattributed",
            href: destinationUrl,
            reason: "existing_fragment",
        };
    }

    if (!Number.isSafeInteger(now) || now < 0) {
        return {
            kind: "unattributed",
            href: destinationUrl,
            reason: "request_id_unavailable",
        };
    }

    let random: string;
    try {
        random = randomId();
    } catch {
        return {
            kind: "unattributed",
            href: destinationUrl,
            reason: "request_id_unavailable",
        };
    }

    if (!/^[a-z0-9-]{16,64}$/iu.test(random)) {
        return {
            kind: "unattributed",
            href: destinationUrl,
            reason: "request_id_unavailable",
        };
    }

    const redirectRequestId = `${now.toString(36)}.${random}`;
    const marker = `wags-short-link:v1:${encodeURIComponent(slug)}:${redirectRequestId}`;
    return {
        kind: "bridged",
        href: `${destinationUrl}#${marker}`,
        redirectRequestId,
        slug,
    };
}
